import type { ChangeEvent } from "react";
import * as Sentry from "@sentry/react";
import {
  ImportFeedbackChrome,
  type ImportFeedbackPayload,
  type ImportFeedbackVariation,
  type ChatBottomSheetPresentation,
} from "@sudowrite/shared-chat";
import Auth from "src/Auth";
import type { ImportFeedbackCohort } from "~shared/importFeedback";
import { Textarea } from "src/sudokit/components/Textarea";

export type { ImportFeedbackPayload, ImportFeedbackVariation };
export type { ImportFeedbackCohort } from "~shared/importFeedback";

type ImportFeedbackProps = {
  variation: ImportFeedbackVariation;
  invocationId: string;
  cohort?: ImportFeedbackCohort;
  onSubmit?: (feedback: ImportFeedbackPayload) => void | Promise<void>;
  onSkip?: () => void;
  disabled?: boolean;
  variant?: "default" | "bottomSheet";
};

function toPresentation(variant: ImportFeedbackProps["variant"]): ChatBottomSheetPresentation {
  return variant === "bottomSheet" ? "bottomSheet" : "default";
}

export function ImportFeedback({
  variation,
  invocationId,
  cohort,
  onSubmit,
  onSkip,
  disabled,
  variant = "default",
}: ImportFeedbackProps) {
  const handleSubmit = (feedback: ImportFeedbackPayload) => {
    onSubmit?.(feedback);
    void Auth.fetchWithIdToken("/api/ai/agenticChat/import-feedback", {
      stars: feedback.rating,
      comment: feedback.feedbackText || undefined,
      variation,
      invocationId,
      cohort,
    }).catch((error) => {
      Sentry.captureException(error);
    });
  };

  return (
    <ImportFeedbackChrome
      variation={variation}
      presentation={toPresentation(variant)}
      onSubmit={handleSubmit}
      onSkip={onSkip}
      disabled={disabled}
      renderFeedbackField={({ value, onChange, disabled: fieldDisabled, autoFocus }) => (
        <Textarea
          mode="default"
          value={value}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => onChange(event.target.value)}
          placeholder="Optional feedback..."
          minRows={3}
          short
          autoFocus={autoFocus}
          disabled={fieldDisabled}
        />
      )}
    />
  );
}
